import { useAuth } from '@agentnet/auth';
import { pxToRem } from '@fluentsms/agentnet-web-components';
import { Menu, MenuItem, Tooltip, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { ArrowDropDownOutlined } from '@material-ui/icons';
import HistoryOutlinedIcon from '@material-ui/icons/HistoryOutlined';
import { saveFileReopen, saveFileStatus } from 'api/file/file-api';
import { fileInfoType } from 'api/file/interfaces/get-file';
import { generateFileSummaryReport } from 'api/pdf/pdf-api';
import clsx from 'clsx';
import { fileStatusFileInformation, KBLinks } from 'features/constants';
import { FileDataContext, FileDataContextInterface } from 'hooks/FileDataContext';
import { ProfileContext, ProfileContextInterface } from 'hooks/ProfileContext';
import useAsync from 'hooks/useAsync';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import PageHeader from 'ui-kit/components/headers/PageHeader';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import AgentNetConfirmFileReopenModal from 'ui-kit/components/modal/AgentNetConfirmFileReopenModal';
import { AgentNetConfirmationDialog } from 'ui-kit/components/modal/ConfirmationDialog';
import useGlobalMessages from 'ui-kit/components/notification/useGlobalMessages';
import useSnackBars from 'ui-kit/components/notification/useSnackbars';
import ContentContainer from 'ui-kit/components/utility/ContentContainer';
import PDFIcon from 'ui-kit/icons/PDF';
import NestedRouteLayout from '../../NestedRouteLayout';
import './FileContent.scss';

export const Files = ({ routes }: never): JSX.Element => {
  const location = useLocation();
  const fileDataCtx: FileDataContextInterface = useContext(FileDataContext) ?? {};
  const {
    fileData,
    loadFileData,
    openPendingCpl,
    setOpenPendingCpl,
    setResetCpl,
    fileDataUpdated,
    setfileDataUpdated,
    hasRemittableCpl,
    setRemmittableCpl,
  } = fileDataCtx;

  const profileCtx: ProfileContextInterface = useContext(ProfileContext) ?? {};
  const { profile, userFirm } = profileCtx;
  // For some paths, we want to hide the tab nav
  const allFilesPath = ['/files'];
  const isNotFilesList = !allFilesPath.includes(location.pathname);
  const isFileInfoPath = location.pathname.endsWith('/file-info');
  const [currentFileStatus, setCurrentFileStatus] = useState(fileStatusFileInformation);
  const isFraudValidationDisabled = false;

  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      root: {
        width: '100%',
        marginTop: pxToRem(113),
        display: 'flex',
        flexDirection: 'column',
      },
      routeContainer: {
        width: '100%',
      },
      list: {
        fontSize: '16px',
      },
      toggleSnapshot: {
        marginRight: theme.spacing(1),
      },
      fileSnapshotPDF: {
        marginLeft: theme.spacing(2),
      },
      reopenFile: {
        background: theme.palette.warning.main,
        marginLeft: theme.spacing(1),
        '&:hover': {
          backgroundColor: theme.palette.warning.dark,
        },
        '& svg': {
          marginRight: theme.spacing(1),
          marginLeft: '-4px',
        },
      },
      loader: {
        minHeight: 'fit-content',
        marginLeft: theme.spacing(1),
      },
      alignRow: {
        display: 'flex',
        alignItems: 'center',
      },
      filesContainer: {
        marginTop: '49px',
        transition: '.3s all',
      },
      filesContainerSnapshotExpanded: {
        marginTop: '0 !important',
      },
      statusDropdown: {
        padding: 0,
        '&>.MuiButton-label .status': {
          fontSize: pxToRem(13),
          color: theme.palette.primary.main,
          fontWeight: 600,
          minWidth: 113,
          textAlign: 'left',
          paddingLeft: 10,
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.statesSecondary.outlinedBorder,
        },
        '& .MuiButton-endIcon': {
          transform: 'scale(1)',
          padding: '6px 9px',
          marginLeft: 0,
        },
        '&.MuiButtonBase-root.Mui-disabled': {
          pointerEvents: 'unset',
          opacity: 0.7,
          cursor: 'not-allowed',
          padding: '5px',
        },
      },
    }),
  );
  const classes = useStyles();
  const { addSnackbarMessage } = useSnackBars();

  const hasActiveCpl = useMemo(() => {
    if (openPendingCpl === undefined || openPendingCpl === -1) {
      return fileData?.serviceStatus?.hasActiveCpl ?? false;
    }
    return openPendingCpl > 0;
  }, [openPendingCpl, fileData]);

  const remmitableCpl = useMemo(() => {
    if (hasRemittableCpl === undefined) {
      return fileData?.serviceStatus?.hasRemittableCpl ?? false;
    }
    return hasRemittableCpl;
  }, [hasRemittableCpl, fileData]);

  enum FileErrorForTooltip {
    duplicateFile = 'File cannot be re-opened as duplicate file # exists within the same Office',
    unreportedProducts = 'File has unreported products. To change the file status: report, void or delete unreported products.',
    noProducts = '',
  }

  useEffect(() => {
    if (fileData?.serviceStatus) {
      const status = ['Opened in Error', 'Cancelled', 'Closed'];

      // Remove 'Closed' Option
      if (fileData.serviceStatus.hasActiveJacket || remmitableCpl || fileData.serviceStatus.hasStandAloneEndorsement) {
        status.pop();
      }

      // Remove 'Opened in Error' and 'Cancelled' Option
      if (fileData.serviceStatus.hasRemittedCpl || fileData.serviceStatus.hasRemittedJacket) {
        status.shift();
        status.shift();
      }

      setCurrentFileStatus(status.map((keys: any) => ({ name: keys, value: keys })));
    }
  }, [fileData?.serviceStatus, hasRemittableCpl]);

  const [statusLoader, setStatusLoader] = useState(false);
  const [tempStatus, setTempStatus] = useState<{ name?: string; id?: number }>({});
  const [statusConfirmationModal, setStatusConfirmationModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [fileStatus, setFileStatus] = useState<{ name?: string; id?: number }>({ name: 'Open', id: 0 });
  const [fileContentError, setFileContentError] = useState<string[]>([]);
  const [fileReopenModal, setFileReopenModal] = useState(false);
  const isFromAddEndorsment = useRef(false);
  const [fileReopenFailed, setFileReopenFailed] = useState(false);
  const [isFileReopenInProgress, setFileReopenInProgress] = useState(false);
  const [downloadingSummary, setDownloadingSummary] = useState(false);

  const { getAccessToken } = useAuth();
  const history = useHistory();

  const changeStatus = async () => {
    const token = await getAccessToken();
    const payload = {
      fileId: fileData?.fileId,
      firmId: fileData?.firmId,
      officeId: fileData?.officeId,
      accountId: fileData?.accountId,
      status: isFromAddEndorsment.current ? 'Open' : fileStatus.name,
      ...(isFromAddEndorsment.current && { reopenByProduct: true }),
      serviceStatus: {
        hasActiveCpl,
        hasActiveJacket: fileData?.serviceStatus?.hasActiveJacket ?? false,
      },
    };
    return saveFileStatus(payload, token);
  };
  const {
    execute: statusExecute,
    value: statusValue,
    status: statusError,
    errors: statusErrorMsg,
  } = useAsync(changeStatus, false);

  const { addGlobalMsg } = useGlobalMessages();

  const downloadReportPdf = async () => {
    setDownloadingSummary(true);
    addSnackbarMessage({
      message: `Generating File Summary Report...`,
      type: 'success',
    });

    const token = await getAccessToken();
    await generateFileSummaryReport(
      fileData?.fileId ?? '',
      fileData?.fileNumber ?? '',
      isFraudValidationDisabled ? '' : userFirm?.firmId ?? '',
      token,
      addGlobalMsg,
    );

    setDownloadingSummary(false);
  };

  const showMessage = (errorMessage: string[]) => {
    errorMessage?.map((err) => {
      addGlobalMsg({
        message: err,
        type: 'error',
      });
    });
    setFileContentError([]);
  };

  useEffect(() => {
    if (fileContentError.length > 0) showMessage(fileContentError);
  }, [fileContentError]);

  useEffect(() => {
    if (Array.isArray(statusErrorMsg) && statusErrorMsg.length > 0 && statusError === 'error') {
      setFileContentError([...statusErrorMsg]);
    }
  }, [statusErrorMsg, statusError]);

  const handleFileStatus = (e: any, i: any) => {
    setTempStatus({ ...fileStatus });
    setFileStatus({ ...i });
    setAnchorEl(null);
    setStatusConfirmationModal(true);
  };

  useEffect(() => {
    if (statusValue) {
      addSnackbarMessage({
        message: isFromAddEndorsment.current ? 'File reopened successfully' : `File saved successfully`,
        type: 'success',
      });
      setResetCpl && setResetCpl(true);
      setStatusLoader(false);
      setStatusConfirmationModal(false);
      setTempStatus({});
      setFileStatus({
        name: statusValue.status,
        id: currentFileStatus.findIndex((status) => status.name === statusValue.status),
      });
      setOpenPendingCpl && setOpenPendingCpl(0);
      setRemmittableCpl && setRemmittableCpl(false);

      loadFileData &&
        loadFileData({ fileId: fileData?.fileId }, () => {
          setFileReopenInProgress(false);
          if (isFromAddEndorsment.current) {
            setFileReopenModal(false);
            isFromAddEndorsment.current = false;
            history.replace({
              pathname: window.location.pathname,
              hash: window.location.hash.includes('#ratesfees') ? window.location.hash : '#ratesfees',
              state: { showRateAndFeeDrawer: true },
            });
          }
        });
    }
  }, [statusValue]);

  const resetLoader = () => {
    setStatusLoader(false);
    setStatusConfirmationModal(false);
    setFileStatus({ ...tempStatus });
    setTempStatus({});
  };

  useEffect(() => {
    if (statusError === 'error') {
      isFromAddEndorsment.current = false;
      setStatusLoader(false);
      setFileReopenInProgress(false);
      setFileStatus({ ...tempStatus });
      setTempStatus({});
      setStatusConfirmationModal(false);
    }
  }, [statusError]);

  useEffect(() => {
    if (fileDataUpdated || fileData) {
      setfileDataUpdated && setfileDataUpdated(false);
      if (fileData?.fileStatus) {
        setFileStatus({
          name: fileData?.fileStatus,
          id: currentFileStatus.findIndex((status) => status.name === fileData?.fileStatus),
        });
      }
      if (fileData?.fileStatusOptions) {
        setCurrentFileStatus(fileData.fileStatusOptions.map((status: any) => ({ name: status, value: status })));
      } else {
        setCurrentFileStatus([]);
      }
    }
  }, [fileData, fileDataUpdated]);

  const hasRemitAccess = useMemo(() => {
    return profile?.activityRights?.some((right) => right.ActivityRightId === 33) ?? false;
  }, [profile?.activityRights]);

  const isReopenEligible = useMemo(() => {
    return fileData?.serviceStatus?.isReopenEligible ?? false;
  }, [fileData?.serviceStatus?.isReopenEligible]);

  const hasFSCRemittedProduct = useMemo(() => {
    return fileData?.serviceStatus?.hasFSCRemittedProduct;
  }, [fileData?.serviceStatus?.hasFSCRemittedProduct]);

  const canReopenFile = useMemo(() => {
    return !fileReopenFailed && fileData?.fileStatus === 'Closed';
  }, [fileData, profile, fileReopenFailed]);

  function onFileReopen() {
    if (isFileReopenInProgress) return;

    setFileReopenModal(true);
  }

  async function onConfirmFileReopen(fileReopenObj: any, type: string) {
    setFileReopenInProgress(true);
    if (type === 'endorsement') {
      isFromAddEndorsment.current = true;
      statusExecute().then();
    } else {
      const payload = {
        fileId: fileData?.fileId,
        reason: fileReopenObj.reason,
      };

      const token = await getAccessToken();
      const response = await saveFileReopen(payload, token);
      setFileReopenInProgress(false);
      setFileReopenModal(false);
      if (response?.result) {
        window.location.reload();
        addSnackbarMessage({
          message: `File reopened successfully`,
          type: 'success',
        });
      } else {
        setFileContentError(response?.messages);
        setFileReopenFailed(true);
      }
    }
  }

  const warningMessage: React.ReactNode = (
    <>
      {!hasActiveCpl && fileData?.serviceStatus?.hasActiveJacket === false ? (
        <Typography>
          If you change the file status, then you will not be able to make future changes to the file.
        </Typography>
      ) : (
        <>
          <Typography>If you change the File Status, it will:</Typography>
          <ul>
            {hasActiveCpl && (
              <>
                <li className={classes.list}>void open CPLs</li>
                <li className={classes.list}>delete pending CPLs</li>
              </>
            )}
            {fileData?.serviceStatus?.hasActiveJacket === true && <li className={classes.list}>void open Jackets</li>}
          </ul>
        </>
      )}
      <Typography
        style={!hasActiveCpl && fileData?.serviceStatus?.hasActiveJacket === false ? { marginTop: '8px' } : {}}
      >
        Press Continue to make the changes or Cancel to discard them.
      </Typography>
    </>
  );

  function getFileErrorStatusTooltip(fileData: fileInfoType | null | undefined) {
    if (!fileData?.canChangeFileStatus && !fileData?.isUniqueFile) {
      return FileErrorForTooltip.duplicateFile;
    } else if (
      !fileData?.canChangeFileStatus &&
      !(fileData?.serviceStatus?.hasActiveCpl || fileData?.serviceStatus?.hasActiveJacket)
    ) {
      return FileErrorForTooltip.unreportedProducts;
    } else {
      return FileErrorForTooltip.noProducts;
    }
  }

  return (
    <ContentContainer fullWidth={!isNotFilesList}>
      {isFileInfoPath && (
        <PageHeader
          title="File Info"
          subtitle="Your central hub to manage all key details for your real estate transaction, including property information, parties involved, and high-level information like sales price, loan amount, and closing date."
          contentRight={
            <>
              <div>
                {canReopenFile ? (
                  <AgentNetButton
                    color="primary"
                    variant="contained"
                    data-testid="ReopenFile"
                    onClick={() => onFileReopen()}
                    className={classes.reopenFile}
                    size="small"
                  >
                    <HistoryOutlinedIcon />
                    Reopen File
                  </AgentNetButton>
                ) : (
                  <>
                    <AgentNetButton
                      data-testid="FileSummaryStatus"
                      variant="outlined"
                      color="primary"
                      disabled={!fileData?.canChangeFileStatus}
                      onClick={(e) => {
                        if (fileStatus.name === 'Open' && currentFileStatus.length > 0) setAnchorEl(e.currentTarget);
                      }}
                      size="small"
                      className={classes.statusDropdown}
                      endIcon={
                        fileStatus.name === 'Open' &&
                        fileData?.canChangeFileStatus &&
                        currentFileStatus.length > 0 && <ArrowDropDownOutlined />
                      }
                    >
                      <Tooltip title={getFileErrorStatusTooltip(fileData as fileInfoType)}>
                        <span className="status">{fileStatus.name}</span>
                      </Tooltip>
                    </AgentNetButton>
                    <Menu
                      id={`status-menu`}
                      anchorEl={anchorEl}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                      open={Boolean(anchorEl) && fileStatus.name === 'Open'}
                      onClose={() => setAnchorEl(null)}
                      keepMounted
                      getContentAnchorEl={null}
                    >
                      {currentFileStatus.map((item, index) => (
                        <MenuItem
                          key={index}
                          onClick={(e) => handleFileStatus(e, { name: item.name, id: index })}
                          selected={item.name === fileStatus.name}
                        >
                          {item.value}
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                )}
                <AgentNetButton
                  color="primary"
                  variant="outlined"
                  data-testid="FileSummaryPDF"
                  onClick={() => downloadReportPdf()}
                  className={classes.fileSnapshotPDF}
                  disabled={downloadingSummary || fileData?.fileId == ''}
                  size="small"
                  startIcon={
                    downloadingSummary ? (
                      <LoadingSpinner
                        className={classes.loader}
                        variant="circle"
                        status={'pending'}
                        showLabel={false}
                        size={16}
                      />
                    ) : (
                      <PDFIcon disabled={downloadingSummary || fileData?.fileId === ''} />
                    )
                  }
                >
                  File Summary
                </AgentNetButton>
              </div>
              <AgentNetConfirmFileReopenModal
                open={fileReopenModal}
                onClose={() => setFileReopenModal(false)}
                onConfirm={onConfirmFileReopen}
                isActionInProgress={isFileReopenInProgress}
                isReopenEligible={isReopenEligible}
                hasFSCRemittedProduct={hasFSCRemittedProduct}
                hasRemitAccess={hasRemitAccess}
              />
            </>
          }
          menuItems={[{ label: 'Knowledge Base', link: KBLinks.fileInfo }]}
        />
      )}

      <NestedRouteLayout
        {...{ routes }}
        className={clsx({
          [classes.routeContainer]: !isNotFilesList,
        })}
      />

      <AgentNetConfirmationDialog
        onConfirm={() => {
          setStatusLoader(true);
          statusExecute().then();
        }}
        open={statusConfirmationModal || false}
        disabled={statusLoader}
        disableCrossIconClick={statusLoader}
        onDismissAction={resetLoader}
        onExitButtonAction={resetLoader}
        dialogTitle="Warning!"
        dialogBtnContent="Continue"
        secondaryActionBtnContent="Cancel"
        dialogTextHTML={warningMessage}
        disableBackDropClick
        qaAttrPrefix="Confirmation"
      />
    </ContentContainer>
  );
};
